import { useState, useRef, Suspense, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { Scene } from './core/scene';
import { VisualizationControls } from './ui/visualization-controls';
import { useDeviceData } from './hooks/use-device-data';
import { ThreeDModels } from '@/lib/threejs/config';
import { useGetDevicesQuery } from '@/services';
import { Button } from '@/components/ui/button';
import {
  Eye,
  EyeOff,
  Clock,
  Link,
  Unlink,
  RefreshCw,
  Settings,
  Play,
  Pause,
  SlidersHorizontal,
  AlertTriangle,
  Maximize2,
  Minimize2,
  Brain,
  Activity,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { Toggle } from '@/components/ui/toggle';

// Time range options, matching other tabs for consistency
const timeRangeOptions = [
  { value: '1', label: '1 hour' },
  { value: '6', label: '6 hours' },
  { value: '24', label: 'Last 24 hours' },
  { value: '72', label: '3 days' },
  { value: '168', label: '7 days' },
  { value: '720', label: '30 days' },
  { value: '1440', label: '60 days' },
  { value: '4320', label: '180 days' },
];

interface ThreeDVisualizationProps {
  className?: string;
  initialDevices?: string[];
  onDeviceSelect?: (deviceId: string, tab?: string) => void;
}

export function ThreeDVisualization({ className, initialDevices = [], onDeviceSelect }: ThreeDVisualizationProps) {
  // State for selected devices and 3D space
  const [selectedDevices, setSelectedDevices] = useState<string[]>(initialDevices);
  const [selectedSpace, setSelectedSpace] = useState<ThreeDModels>(ThreeDModels.EV_CHARGER);
  const [showControls, setShowControls] = useState<boolean>(true);
  const [timeRange, setTimeRange] = useState<string>('24'); // Default to 24 hours
  const [modelAttachment, setModelAttachment] = useState<boolean>(true); // Default to attaching sensors to models
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [timeSlider, setTimeSlider] = useState<number>(100); // 0-100 scale for timeline
  const [liveDataMode, setLiveDataMode] = useState<boolean>(false);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);
  const [isEditorMode, setIsEditorMode] = useState(false);
  const [sensorTypeFilters, setSensorTypeFilters] = useState<string[]>([]);

  // Performance settings
  const [showPerformanceSettings, setShowPerformanceSettings] = useState<boolean>(false);
  const [performanceSettings, setPerformanceSettings] = useState({
    shadows: true,
    highQuality: true,
    antialias: true,
    fps: true,
  });

  // Heatmap state
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showAIInsights, setShowAIInsights] = useState<boolean>(false);

  // Add ref to access scene methods
  const sceneRef = useRef(null);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Function to reset camera view
  const resetCameraView = () => {
    if (sceneRef.current) {
      sceneRef.current.resetCameraView();
    }
  };

  // Fetch all devices for the selector
  const { data: devicesData, refetch: refetchDevices } = useGetDevicesQuery({
    includeSensors: 'true',
  });

  // Get sensor data for selected devices with time range
  const { sensorData, isLoading, alerts } = useDeviceData(selectedDevices, timeRange);

  // Set up auto-refresh when live data mode is active
  useEffect(() => {
    if (liveDataMode) {
      setRefreshInterval(5000);
      toast.success('Live data mode activated', {
        description: 'Data will automatically update every 5 seconds',
      });
    } else {
      setRefreshInterval(null);
    }
  }, [liveDataMode]);

  // Handle auto-refresh
  useEffect(() => {
    if (!refreshInterval) return;

    const intervalId = setInterval(() => {
      refetchDevices();
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [refreshInterval, refetchDevices]);

  // Get the label for the currently selected time range
  const selectedTimeRangeLabel =
    timeRangeOptions.find((option) => option.value === timeRange)?.label || 'Last 24 hours';

  // Handle time slider change
  const handleTimeSliderChange = (value: number[]) => {
    setTimeSlider(value[0]);
    // Pause playback when manually adjusting
    if (isPlaying) setIsPlaying(false);
  };

  // Toggle play/pause for timeline animation
  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  // Auto-increment time slider when playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setTimeSlider((prev) => {
        const next = prev + 1;
        if (next > 100) {
          setIsPlaying(false);
          return 100;
        }
        return next;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Calculate alerts count
  const alertsCount =
    alerts?.total || sensorData.filter((sensor) => sensor.status === 'warning' || sensor.status === 'critical').length;

  // Enhanced device selection handler with tab navigation
  const handleDeviceSelection = (deviceIds: string[]) => {
    setSelectedDevices(deviceIds);
    // If parent component wants notifications about device selection
    if (onDeviceSelect && deviceIds.length === 1) {
      onDeviceSelect(deviceIds[0]);
    }
  };

  // Handle navigation to alerts tab for a specific device
  const handleDeviceAlert = (deviceId: string, tab?: string) => {
    if (onDeviceSelect) {
      onDeviceSelect(deviceId, tab);
    }
  };

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
    // Force resize after a small delay to ensure DOM updates
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Force resize after fullscreen change
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* 3D Visualization */}
      <div className={`${isFullscreen ? 'w-screen h-screen' : 'w-full h-[600px]'} rounded-lg border overflow-hidden`}>
        <Canvas
          shadows={performanceSettings.shadows}
          dpr={performanceSettings.highQuality ? [1, 2] : [1, 1]}
          gl={{ antialias: performanceSettings.antialias }}
          camera={{ position: [15, 15, 15], fov: 60 }}
        >
          <Suspense fallback={null}>
            <Scene
              ref={sceneRef}
              sensorData={sensorData}
              selectedSpace={selectedSpace}
              modelAttachment={modelAttachment}
              deviceIds={selectedDevices}
              onDeviceSelect={handleDeviceAlert}
              isEditorMode={isEditorMode}
              sensorTypeFilters={sensorTypeFilters}
              showHeatmap={showHeatmap}
            />
          </Suspense>
          {!performanceSettings.fps && <Stats className="hidden" />}
        </Canvas>
      </div>

      {/* Timeline controls */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 w-2/3 max-w-3xl bg-background/80 backdrop-blur-md p-3 rounded-lg shadow border">
        <div className="flex items-center gap-3 mb-2">
          <Button
            size="sm"
            variant="ghost"
            className="w-8 h-8 p-0"
            onClick={togglePlayback}
            title={isPlaying ? 'Pause' : 'Play'}
            disabled={liveDataMode}
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </Button>

          <Slider
            value={[timeSlider]}
            min={0}
            max={100}
            step={1}
            onValueChange={handleTimeSliderChange}
            className="flex-1"
            disabled={liveDataMode}
          />

          <span className="text-xs text-muted-foreground min-w-[60px]">
            {timeSlider === 100 ? 'Latest' : `${timeSlider}%`}
          </span>
        </div>
        <div className="flex justify-between text-xs text-muted-foreground px-10">
          <span>Past</span>
          <span>Timeline</span>
          <span>Now</span>
        </div>
      </div>

      {/* Control buttons row - top left */}
      <div className="absolute top-4 left-4 flex gap-2 items-center flex-wrap">
        {/* Live data toggle */}
        <div className="bg-background/90 backdrop-blur-sm px-2 py-1.5 rounded-lg shadow border flex items-center gap-2 mr-2">
          <Switch id="live-mode" checked={liveDataMode} onCheckedChange={setLiveDataMode} />
          <Label htmlFor="live-mode" className="text-xs cursor-pointer flex items-center">
            <div
              className={`h-2 w-2 rounded-full ${liveDataMode ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground'} mr-1.5`}
            ></div>
            Live Data
          </Label>
        </div>

        {/* Time range selector */}
        <div className="bg-background/90 backdrop-blur-sm p-2 rounded-lg shadow border flex items-center gap-2">
          <Clock size={16} className="text-muted-foreground" />
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px] h-8 text-sm border-none bg-transparent focus:ring-0">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Link toggle - whether to attach sensors to model */}
        <Button
          size="sm"
          variant={modelAttachment ? 'default' : 'outline'}
          className="gap-1"
          onClick={() => setModelAttachment(!modelAttachment)}
          title={modelAttachment ? 'Detach sensors from model' : 'Attach sensors to model'}
        >
          {modelAttachment ? (
            <>
              <Link size={14} /> Attached
            </>
          ) : (
            <>
              <Unlink size={14} /> Detached
            </>
          )}
        </Button>

        {/* Reset view button */}
        <Button size="sm" variant="outline" className="gap-1" onClick={resetCameraView} title="Reset camera view">
          <RefreshCw size={14} />
          Reset View
        </Button>

        {/* Performance settings */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1">
              <Settings size={14} />
              <span className="sm:hidden md:inline">Settings</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Performance Settings</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="p-2 space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="shadows" className="text-sm">
                  Shadows
                </Label>
                <Switch
                  id="shadows"
                  checked={performanceSettings.shadows}
                  onCheckedChange={(checked) => setPerformanceSettings((prev) => ({ ...prev, shadows: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="quality" className="text-sm">
                  High Quality
                </Label>
                <Switch
                  id="quality"
                  checked={performanceSettings.highQuality}
                  onCheckedChange={(checked) => setPerformanceSettings((prev) => ({ ...prev, highQuality: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="antialias" className="text-sm">
                  Anti-aliasing
                </Label>
                <Switch
                  id="antialias"
                  checked={performanceSettings.antialias}
                  onCheckedChange={(checked) => setPerformanceSettings((prev) => ({ ...prev, antialias: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="fps" className="text-sm">
                  Show FPS
                </Label>
                <Switch
                  id="fps"
                  checked={performanceSettings.fps}
                  onCheckedChange={(checked) => setPerformanceSettings((prev) => ({ ...prev, fps: checked }))}
                />
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
        {/* AI Insights toggle */}
        <Button
          size="sm"
          variant={showAIInsights ? 'default' : 'outline'}
          className="gap-1"
          onClick={() => setShowAIInsights(!showAIInsights)}
          title="Toggle AI insights"
        >
          <Brain size={14} />
          AI Insights
        </Button>
      </div>

      {/* Fullscreen toggle button */}
      <Button
        size="icon"
        variant="outline"
        className="absolute top-4 right-20 w-10 h-10 rounded-full shadow-md bg-background"
        onClick={toggleFullscreen}
        title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      >
        {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
      </Button>

      {/* Toggle button for controls panel */}
      <Button
        size="icon"
        variant="outline"
        className="absolute top-4 right-4 w-10 h-10 rounded-full shadow-md bg-background"
        onClick={() => setShowControls(!showControls)}
        title={showControls ? 'Hide controls' : 'Show controls'}
      >
        {showControls ? <EyeOff size={18} /> : <Eye size={18} />}
      </Button>

      {/* Controls Panel */}
      {showControls && (
        <div className="absolute top-20 right-4 w-80">
          <VisualizationControls
            devices={devicesData?.data || []}
            selectedDevices={selectedDevices}
            setSelectedDevices={handleDeviceSelection}
            selectedSpace={selectedSpace}
            setSelectedSpace={setSelectedSpace}
            onEditorModeChange={setIsEditorMode}
            onSensorTypeFilterChange={setSensorTypeFilters}
            showHeatmap={showHeatmap}
            onHeatmapChange={setShowHeatmap}
          />
        </div>
      )}

      {/* Info display for selected devices */}
      <div className="absolute bottom-4 left-4 bg-background/80 backdrop-blur-sm p-3 rounded-lg shadow border max-w-sm">
        <div className="text-sm font-medium">
          {selectedDevices.length === 0 ? (
            'No devices selected. Use the filter panel to select devices.'
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-primary">{selectedDevices.length}</span> device(s) selected with{' '}
                <span className="text-primary">{sensorData.length}</span> sensor(s)
                {alertsCount > 0 && (
                  <Badge variant="destructive" className="text-xs h-5 ml-1 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    {alertsCount} Alert{alertsCount !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <span>
                  {liveDataMode ? (
                    <>
                      <span className="inline-flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse"></span>
                        Live Data
                      </span>
                    </>
                  ) : (
                    <>Showing data from: {selectedTimeRangeLabel}</>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Insights overlay */}
      {showAIInsights && selectedDevices.length > 0 && (
        <div className="absolute top-16 left-4 w-80 bg-background/90 backdrop-blur-sm p-4 rounded-lg shadow border">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-4 w-4 text-blue-500" />
            <span className="font-medium text-sm">AI Analysis</span>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Overall Health:</span>
              <Badge variant="outline" className="text-green-600">
                <Activity className="h-3 w-3 mr-1" />
                92% Good
              </Badge>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Anomalies Detected:</span>
              <span className="text-yellow-600 font-medium">2 Minor</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Optimization Potential:</span>
              <span className="text-blue-600 font-medium">15% Energy</span>
            </div>

            <div className="pt-2 border-t text-xs text-muted-foreground">
              AI is continuously monitoring your devices for patterns, anomalies, and optimization opportunities.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
